/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ComponentProps } from 'react';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';

/**
 * Invisible full-bleed link, stacked behind visible content, that makes an entire card/hero
 * clickable to the same destination as a nested, still-independently-clickable CTA.
 *
 * Usage: render as a sibling positioned before the content layer (image, then StretchedLink,
 * then content). The content layer must be `pointer-events-none` with `pointer-events-auto`
 * re-enabled on the CTA itself, so clicks on the CTA hit the CTA and clicks anywhere else
 * (including the image) fall through to this link. `aria-hidden` + `tabIndex={-1}` keep it out
 * of the accessibility tree and tab order — the CTA remains the single accessible link to this
 * destination, so screen reader / keyboard users aren't given a second, unlabeled stop.
 */
export function StretchedLink({ className, ...rest }: ComponentProps<typeof Link>) {
    return <Link aria-hidden="true" tabIndex={-1} className={cn('absolute inset-0', className)} {...rest} />;
}
